import React, { useState, useEffect, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import ModalBasic from "./ModalBasic.jsx";
import RoleSelectionModal from "./RoleSelectionModal.jsx";
import TabbedNavigation from "./TabbedNavigation.jsx";
import { addToast } from "../store/app/action.js";
import { FaCrown } from "react-icons/fa6";
import CanView from "./CanView.jsx";
import { setSelectedMerchant } from "@/store/app/action.js";

function ImpersonationModal({ modalOpen, setModalOpen }) {
  const dispatch = useDispatch();
  const { merchants, user } = useSelector((state) => state.app);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [roleModalOpen, setRoleModalOpen] = useState(false);
  const [selectedLocation, setSelectedLocation] = useState(null);
  const [storeType, setStoreType] = useState("direct"); // 'direct' or 'marketplace'
  const searchInputRef = useRef(null);

  // Flatten all locations from all merchants for search
  const allLocations = merchants
    .filter((merchant) => {
      if (storeType === "direct") {
        return merchant.direct;
      } else {
        return !merchant.direct;
      }
    })
    .flatMap((merchant) =>
      merchant.locations.map((location) => ({
        ...location,
        merchantName: merchant.name,
        merchantId: merchant.id,
      }))
    );

  // Filter locations based on store type (Direct vs Marketplace)

  // Filter locations based on search term
  const filteredLocations = allLocations.filter((location) => {
    if (!searchTerm) return true;
    const term = searchTerm.toLowerCase();
    return (
      location.store_id?.toLowerCase().includes(term) ||
      location.name?.toLowerCase().includes(term) ||
      location.merchantName?.toLowerCase().includes(term)
    );
  });

  // Reset selected index when search changes or store type changes
  useEffect(() => {
    setSelectedIndex(0);
  }, [searchTerm, storeType]);

  // Focus search input when modal opens
  useEffect(() => {
    if (modalOpen) {
      // Use requestAnimationFrame to ensure DOM is ready, then wait for transition
      const focusElement = () => {
        requestAnimationFrame(() => {
          setTimeout(() => {
            if (searchInputRef.current) {
              searchInputRef.current.focus();
              // Additional attempt after a short delay
              setTimeout(() => {
                if (searchInputRef.current && document.activeElement !== searchInputRef.current) {
                  searchInputRef.current.focus();
                }
              }, 100);
            }
          }, 300); // Increased delay to account for transition
        });
      };
      
      focusElement();
    }
  }, [modalOpen]);

  // Handle keyboard navigation
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (!modalOpen || roleModalOpen) return;

      // Tab switching shortcuts
      if (e.ctrlKey) {
        if (e.key === "d") {
          e.preventDefault();
          setStoreType("direct");
          setSelectedIndex(0);
          // Refocus search field when switching tabs via keyboard
          setTimeout(() => {
            if (searchInputRef.current) {
              searchInputRef.current.focus();
            }
          }, 0);
        } else if (e.key === "m") {
          e.preventDefault();
          setStoreType("marketplace");
          setSelectedIndex(0);
          // Refocus search field when switching tabs via keyboard
          setTimeout(() => {
            if (searchInputRef.current) {
              searchInputRef.current.focus();
            }
          }, 0);
        }
        return;
      }

      if (e.key === "ArrowDown") {
        e.preventDefault();
        setSelectedIndex((prev) =>
          prev < filteredLocations.length - 1 ? prev + 1 : 0
        );
      } else if (e.key === "ArrowUp") {
        e.preventDefault();
        setSelectedIndex((prev) =>
          prev > 0 ? prev - 1 : filteredLocations.length - 1
        );
      } else if (e.key === "Enter") {
        e.preventDefault();
        if (filteredLocations[selectedIndex]) {
          if (e.shiftKey) {
            handleRoleSelection(filteredLocations[selectedIndex]);
          } else {
            handleImpersonate(filteredLocations[selectedIndex]);
          }
        }
      } else if (e.key === "Escape") {
        setModalOpen(false);
      }
    };

    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [modalOpen, roleModalOpen, selectedIndex, filteredLocations]);

  const handleImpersonate = (location, role = null) => {
    dispatch(setSelectedMerchant(location.merchantId));
    dispatch({
      type: "SET_SELECTED_LOCATION",
      payload: location.id,
    });

    // Handle role-based impersonation
    if (role && role.id !== "saucey_admin") {
      // Map role types to permission strings
      const rolePermissionMap = {
        store_admin: "admin",
        store_manager: "manager",
        store_clerk: "clerk",
      };

      const permissionString = rolePermissionMap[role.id];

      if (permissionString && user) {
        // Create new permissions structure for the selected merchant and location
        const newPermissions = [
          {
            merchant: location.merchantId,
            locations: [
              {
                location: location.id,
                permissions: [permissionString],
                _id: `temp_${Date.now()}`, // Generate temporary ID
              },
            ],
            type: "user",
            _id: `temp_${Date.now()}`, // Generate temporary ID
          },
        ];
        // Update user permissions and type in Redux
        dispatch({
          type: "UPDATE_USER_PERMISSIONS",
          payload: newPermissions,
        });

        // Update user type from saucey_admin to user
        dispatch({
          type: "UPDATE_USER_TYPE",
          payload: "user",
        });

        // Filter merchants to only show the selected merchant
        const selectedMerchant = merchants.find(
          (m) => m.id === location.merchantId
        );
        if (selectedMerchant) {
          dispatch({
            type: "FILTER_MERCHANTS_BY_ROLE",
            payload: [selectedMerchant],
          });
        }

        // Update current role to reflect the new permissions
        dispatch({
          type: "UPDATE_CURRENT_ROLE",
          payload: [permissionString],
        });
      }
    }

    const message =
      role && role.id !== "saucey_admin"
        ? `Session set to ${location.name} as ${role.name}, refresh to restore privileges.`
        : `Session set to ${location.name}`;

    dispatch(
      addToast({
        type: "success",
        message,
        timer: 5,
      })
    );
    setModalOpen(false);
    setSearchTerm("");
  };

  const handleRoleSelection = (location) => {
    setSelectedLocation(location);
    setRoleModalOpen(true);
  };

  const handleRoleSelect = (location, role) => {
    handleImpersonate(location, role);
  };

  const handleClose = () => {
    setModalOpen(false);
    setSearchTerm("");
    setSelectedIndex(0);
    setStoreType("direct"); // Reset to direct tab when closing
  };

  return (
    <>
      <ModalBasic
        title={
          <div className="flex items-center">
            Impersonate as store
            <FaCrown className="ml-1 w-3 h-3" />
          </div>
        }
        modalOpen={modalOpen}
        setModalOpen={handleClose}
        width="lg"
      >
        <div className="space-y-4">
          {/* Store Type Tabs */}
          <div className="flex items-center">
            <TabbedNavigation
              tabs={[
                { id: "direct", label: "Direct", shortcut: ["Ctrl", "D"] },
                {
                  id: "marketplace",
                  label: "Marketplace",
                  shortcut: ["Ctrl", "M"],
                },
              ]}
              activeTab={storeType}
              onTabChange={(newStoreType) => {
                setStoreType(newStoreType);
                setSelectedIndex(0); // Reset selection when switching tabs
                // Refocus search field when switching tabs
                setTimeout(() => {
                  if (searchInputRef.current) {
                    searchInputRef.current.focus();
                  }
                }, 0);
              }}
              variant="default"
              size="sm"
            />
          </div>

          {/* Search Input */}
          <div>
            <input
              ref={searchInputRef}
              type="text"
              placeholder="Search by store ID, name, or merchant..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              autoFocus={modalOpen}
              className="w-full px-3 py-2 border border-slate-300 dark:border-slate-600 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-slate-700 dark:text-white"
            />
          </div>

          {/* Results List */}
          <div className="max-h-96 overflow-y-auto border border-slate-200 dark:border-slate-600 rounded-md">
            {filteredLocations.length === 0 ? (
              <div className="p-4 text-center text-slate-500 dark:text-slate-400">
                No stores found
              </div>
            ) : (
              filteredLocations.map((location, index) => (
                <div
                  key={`${location.merchantId}-${location.id}`}
                  className={`p-3 cursor-pointer border-b border-slate-100 dark:border-slate-700 last:border-b-0 ${
                    index === selectedIndex
                      ? "bg-blue-50 dark:bg-blue-900/20"
                      : "hover:bg-slate-50 dark:hover:bg-slate-700/50"
                  }`}
                  onClick={(e) => {
                    if (e.shiftKey) {
                      handleRoleSelection(location);
                    } else {
                      handleImpersonate(location);
                    }
                  }}
                >
                  <div className="flex justify-between items-start">
                    <div>
                      <div className="font-medium text-slate-900 dark:text-slate-100">
                        {location.name}
                      </div>
                      <div className="text-sm text-slate-500 dark:text-slate-400">
                        {location.merchantName}
                      </div>
                    </div>
                    <div className="text-sm text-slate-400 dark:text-slate-500">
                      {location.store_id}
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>

          {/* Instructions */}
          <CanView webOnly>
            <div className="text-xs text-slate-500 dark:text-slate-400">
              Use ↑↓ arrows to navigate, Enter to select, or click to
              impersonate. Hold Shift + click or Enter to select a role.
              <br />
              Use Ctrl+D for Direct tab, Ctrl+M for Marketplace tab.
            </div>
          </CanView>
        </div>
      </ModalBasic>

      <RoleSelectionModal
        modalOpen={roleModalOpen}
        setModalOpen={setRoleModalOpen}
        location={selectedLocation}
        onRoleSelect={handleRoleSelect}
      />
    </>
  );
}

export default ImpersonationModal;
